/**
 * Design Tokens - Extracted from Figma Design System
 * Source: https://www.figma.com/design/nBtT8RXKaaqEn83HWDRnfz/Dev-Doc-Page
 *
 * Light Theme: node-id=1-6658
 * Dark Theme: node-id=1-6659
 */

// =============================================================================
// SPACING TOKENS
// =============================================================================
export const spacing = {
  gap0x: '0px',
  gap1x: '5px',
  gap2x: '10px',
  gap3x: '15px',
  gap4x: '20px',
  gap5x: '25px',
  gap6x: '30px',
  gap8x: '40px',
  gap10x: '50px',
  gap20x: '100px',
};

// Numeric values for calculations
export const spacingValues = {
  gap0x: 0,
  gap1x: 5,
  gap2x: 10,
  gap3x: 15,
  gap4x: 20,
  gap5x: 25,
  gap6x: 30,
  gap8x: 40,
  gap10x: 50,
  gap20x: 100,
};

// =============================================================================
// BORDER RADIUS TOKENS
// =============================================================================
export const borderRadius = {
  square: '0px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  round: '1000px',
};

// Numeric values
export const borderRadiusValues = {
  square: 0,
  md: 6,
  lg: 8,
  xl: 12,
  round: 1000,
};

// =============================================================================
// SIZE TOKENS
// =============================================================================
export const sizes = {
  heightMD: '40px',
  headerHeight: '90px',
  headerHeightMobile: '70px',
};

// =============================================================================
// TYPOGRAPHY TOKENS
// =============================================================================
export const fontFamily = {
  primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  mono: "'Input Mono', 'SF Mono', 'Roboto Mono', Menlo, monospace",
  secondary: "'Lato', sans-serif",
};

export const fontWeight = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
};

export const fontSize = {
  xs: '12px',
  sm: '14px',      // SM-1
  base: '16px',    // BASE, BLOG
  lg: '18px',      // LG-1
  xl: '21px',      // LG-2-B
  '2xl': '24px',   // LG-3-B
  '3xl': '28px',   // LG-4-B
};

export const lineHeight = {
  tight: 1.3,
  normal: 1.5,
  relaxed: 1.625,
  blog: '30px',
};

// Typography presets matching Figma styles
export const typography = {
  // Small variants
  'sm-1': {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.tight,
  },
  'sm-1-b': {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semiBold,
    lineHeight: lineHeight.tight,
  },
  // Base variants
  base: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.tight,
  },
  blog: {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.base,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.blog,
    letterSpacing: '0.08px',
  },
  // Large variants
  'lg-1': {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.lg,
    fontWeight: fontWeight.medium,
    lineHeight: lineHeight.tight,
  },
  'lg-1-b': {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.lg,
    fontWeight: fontWeight.semiBold,
    lineHeight: lineHeight.tight,
  },
  'lg-2-b': {
    fontFamily: fontFamily.primary,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.semiBold,
    lineHeight: lineHeight.tight,
  },
  'lg-3-b': {
    fontFamily: fontFamily.primary,
    fontSize: fontSize['2xl'],
    fontWeight: fontWeight.semiBold,
    lineHeight: lineHeight.tight,
  },
  'lg-4-b': {
    fontFamily: fontFamily.primary,
    fontSize: fontSize['3xl'],
    fontWeight: fontWeight.semiBold,
    lineHeight: lineHeight.tight,
  },
};

// =============================================================================
// COLOR TOKENS - LIGHT THEME
// =============================================================================
export const lightColors = {
  // Primary
  primary: '#04b17d',

  // Background colors
  bg: '#f7f7f7',
  bgContainer: '#ffffff',
  bgBox: 'rgba(29, 28, 33, 0.04)',
  bgSurface: '#f1f1f3',
  bgGreyContainer: '#e8e8e8',

  // Border colors
  border: 'rgba(0, 0, 0, 0.1)',
  borderSecondary: 'rgba(0, 0, 0, 0.04)',

  // Text/Info colors
  info: '#1e1d29',           // Primary text
  infoSecondary: '#3f424a',  // Secondary text
  infoTertiary: '#727684',   // Tertiary text
  infoQuaternary: '#a1a0ab', // Quaternary text (muted)

  // Semantic colors
  link: '#04b17d',
  linkHover: '#038f64',

  // Code block
  codeBackground: '#f1f1f3',
  codeText: '#04b17d',

  // Table
  tableRowAlt: '#f8f8f8',
  tableBorder: '#cccccc',

  // Diff colors
  diffAddBg: '#f0fff4',
  diffAddText: '#22863a',
  diffRemoveBg: '#ffcccc',
  diffRemoveText: '#ff0000',
};

// =============================================================================
// COLOR TOKENS - DARK THEME
// =============================================================================
export const darkColors = {
  // Primary
  primary: '#04b17d',

  // Background colors
  bg: '#0c0e13',
  bgContainer: '#181a1f',
  bgBox: 'rgba(255, 255, 255, 0.06)',
  bgSurface: '#1f2126',
  bgGreyContainer: '#181a1f',

  // Border colors
  border: '#3d3f48',
  borderSecondary: '#25272c',

  // Text/Info colors
  info: 'rgba(255, 255, 255, 0.9)',  // Primary text
  infoSecondary: '#c9c7d0',           // Secondary text
  infoTertiary: '#8f92a3',            // Tertiary text
  infoQuaternary: '#7b798f',          // Quaternary text (muted)

  // Semantic colors
  link: '#04b17d',
  linkHover: '#1ed3c6',

  // Code block
  codeBackground: '#1f2126',
  codeText: '#04b17d',

  // Table
  tableRowAlt: '#1f2126',
  tableBorder: '#3d3f48',

  // Diff colors
  diffAddBg: '#0d2818',
  diffAddText: '#4ade80',
  diffRemoveBg: '#2d1b1b',
  diffRemoveText: '#f87171',
};

// =============================================================================
// BREAKPOINTS
// =============================================================================
export const breakpoints = {
  xs: '576px',   // <= 576px (mobile)
  sm: '577px',   // 577-768px (small tablet)
  md: '769px',   // 769-992px (tablet)
  lg: '993px',   // 993-1440px (desktop)
  xl: '1441px',  // 1441-1920px (large desktop)
  '2xl': '1921px', // > 1920px (extra large)
};

export const breakpointValues = {
  xs: 576,
  sm: 577,
  md: 769,
  lg: 993,
  xl: 1441,
  '2xl': 1921,
};

// Media query helpers
export const media = {
  xs: `@media (max-width: ${breakpoints.xs})`,
  sm: `@media (min-width: ${breakpoints.sm}) and (max-width: 768px)`,
  md: `@media (min-width: ${breakpoints.md}) and (max-width: 992px)`,
  lg: `@media (min-width: ${breakpoints.lg}) and (max-width: 1440px)`,
  xl: `@media (min-width: ${breakpoints.xl}) and (max-width: 1920px)`,
  '2xl': `@media (min-width: ${breakpoints['2xl']})`,
  // Common shortcuts
  mobile: `@media (max-width: ${breakpoints.xs})`,
  tablet: `@media (min-width: ${breakpoints.sm}) and (max-width: 992px)`,
  desktop: `@media (min-width: ${breakpoints.lg})`,
};

// =============================================================================
// Z-INDEX LAYERS
// =============================================================================
export const zIndex = {
  dropdown: 100,
  sticky: 200,
  modal: 300,
  tooltip: 400,
  header: 500,
  overlay: 600,
};

// =============================================================================
// TRANSITIONS
// =============================================================================
export const transitions = {
  fast: '0.15s ease',
  normal: '0.3s ease',
  slow: '0.5s ease',
};

// =============================================================================
// SHADOWS
// =============================================================================
export const shadows = {
  light: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 1px 4px 0 rgba(0, 0, 0, 0.1)',
    lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    header: '0 1px 1px 0 rgba(116, 129, 141, 0.1)',
  },
  dark: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    md: '0 1px 4px 0 rgba(0, 0, 0, 0.4)',
    lg: '0 4px 6px -1px rgba(0, 0, 0, 0.5)',
    header: '-1px 0px 4px 1px rgba(175, 158, 232, 0.4)',
  },
};

// =============================================================================
// COMPLETE THEME OBJECTS
// =============================================================================
export const lightTheme = {
  colors: lightColors,
  spacing,
  borderRadius,
  typography,
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  sizes,
  shadows: shadows.light,
  transitions,
  zIndex,
  breakpoints,
  media,
};

export const darkTheme = {
  colors: darkColors,
  spacing,
  borderRadius,
  typography,
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
  sizes,
  shadows: shadows.dark,
  transitions,
  zIndex,
  breakpoints,
  media,
};

// Default export
export default {
  light: lightTheme,
  dark: darkTheme,
  spacing,
  borderRadius,
  typography,
  breakpoints,
  media,
  zIndex,
  transitions,
};
